import { useCallback, useMemo, useRef, useState } from "react";
import { message } from "antd";
import type { AxiosError } from "axios";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { checkListExists, uploadListAPI } from "src/services/EngagementAPIs";
import { previewListAPI } from "src/services/AudienceListAPIs";
import type { PreviewListResponse } from "src/layouts/Engagements/EngagementInterfaces";
import { getTenantId } from "src/utils/getters";
import { formatNumber } from "src/utils/numberFormat";
import {
  AUDIENCE_ROUTE,
  CREATE_LIST_STEPS,
  CSV_EXTENSION,
  EMPTY_MAPPING,
  LIST_FIELDS,
  MAX_FILE_ROWS,
  MAX_FILE_SIZE_BYTES,
  UPLOADED_LIST_TYPE,
  type ColumnMapping,
  type ListFieldKey,
} from "./createList.constants";
import {
  buildCsvFile,
  buildUploadFile,
  cleanupRows,
  parseCsvFile,
  suggestMapping,
  type ParsedCsv,
} from "./createListCsv";
import { useCreateListLabel } from "./useCreateListLabel";
import { useAudienceTags } from "../Shared/UseAudienceTags";

const SKIPPED_ROWS_FILE_NAME = "skipped_rows.csv";

const getCustomTags = (tags: string[], availableTags: string[]): string[] => {
  const existing = new Set(availableTags.map(tag => tag.toLowerCase()));
  return tags.filter(tag => !existing.has(tag.toLowerCase()));
};

export const useCreateList = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const label = useCreateListLabel();

  const [stepIndex, setStepIndex] = useState(0);
  const [csv, setCsv] = useState<ParsedCsv | null>(null);
  const [parsing, setParsing] = useState(false);
  const [mapping, setMapping] = useState<ColumnMapping>(EMPTY_MAPPING);
  const [listName, setListName] = useState("");
  const [description, setDescription] = useState("");
  const [checkingName, setCheckingName] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<PreviewListResponse | null>(null);
  const [creating, setCreating] = useState(false);
  const [isCreated, setIsCreated] = useState(false);
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const isAdvancingRef = useRef(false);
  const { tags, availableTags, addTag, removeTag } = useAudienceTags();

  const stepKey = CREATE_LIST_STEPS[stepIndex].key;

  const cleanup = useMemo(
    () => (csv && mapping.email ? cleanupRows(csv, mapping) : null),
    [csv, mapping]
  );

  const peopleCount = cleanup?.keptRows.length ?? 0;
  const phoneCount = mapping.phone && cleanup ? peopleCount - cleanup.missingPhoneCount : 0;
  const hasRequiredMapping = LIST_FIELDS.every(field => !field.required || mapping[field.key]);

  const canProceed = {
    upload: Boolean(csv) && !parsing,
    match: hasRequiredMapping && peopleCount > 0,
    details: Boolean(listName.trim()) && !checkingName && !previewing,
    review: !creating,
  }[stepKey];

  const selectFile = useCallback(
    async (file: File) => {
      if (!file.name.toLowerCase().endsWith(CSV_EXTENSION)) {
        message.warning(label("errors.onlyCsv"));
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        message.error(label("errors.fileTooLarge"));
        return;
      }

      setParsing(true);
      try {
        const parsed = await parseCsvFile(file);
        if (!parsed.headers.length || !parsed.rows.length) {
          message.error(label("errors.emptyFile"));
          return;
        }
        if (parsed.rows.length > MAX_FILE_ROWS) {
          message.error(label("errors.tooManyRows"));
          return;
        }
        setCsv(parsed);
        setMapping(suggestMapping(parsed.headers));
        setDescription(
          label("details.descriptionDraft", {
            fileName: file.name,
            count: formatNumber(parsed.rows.length, {}, i18n.language),
            date: new Intl.DateTimeFormat(i18n.language, { month: "short", year: "numeric" }).format(
              new Date()
            ),
          })
        );
      } catch {
        message.error(label("errors.parseFailed"));
      } finally {
        setParsing(false);
      }
    },
    [label, i18n.language]
  );

  const removeFile = useCallback(() => {
    setCsv(null);
    setMapping(EMPTY_MAPPING);
    setDescription("");
  }, []);

  const setFieldMapping = useCallback((field: ListFieldKey, header: string | null) => {
    setMapping(prev => {
      const next = { ...prev, [field]: header };
      const swappedField = LIST_FIELDS.find(
        item => item.key !== field && header && prev[item.key] === header
      );
      if (swappedField) {
        next[swappedField.key] = prev[field];
      }
      return next;
    });
  }, []);

  const downloadSkippedRows = useCallback(() => {
    if (csv && cleanup?.skippedRows.length) {
      saveAs(buildCsvFile(csv.headers, cleanup.skippedRows, SKIPPED_ROWS_FILE_NAME));
    }
  }, [csv, cleanup]);

  const downloadList = useCallback(() => {
    if (csv && cleanup) {
      saveAs(buildUploadFile(csv, mapping, cleanup.keptRows), `${listName.trim() || csv.file.name}.csv`);
    }
  }, [csv, cleanup, mapping, listName]);

  const verifyListName = async () => {
    setCheckingName(true);
    try {
      await checkListExists({ tenant_id: Number(getTenantId()), list_name: listName.trim() });
      return true;
    } catch (error) {
      const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      message.error(detail || label("errors.listNameExists"));
      return false;
    } finally {
      setCheckingName(false);
    }
  };

  const buildListFormData = (): FormData | null => {
    const userId = localStorage.getItem("user_id");
    if (!csv || !cleanup || !userId) {
      message.error(label("errors.missingSession"));
      return null;
    }

    const formData = new FormData();
    formData.append("tenant_id", getTenantId());
    formData.append("user_id", userId);
    formData.append("list_name", listName.trim());
    formData.append("list_type", UPLOADED_LIST_TYPE);
    formData.append("members", String(peopleCount));
    if (description.trim()) {
      formData.append("description", description.trim());
    }
    LIST_FIELDS.forEach(field => {
      const header = mapping[field.key];
      if (header) {
        formData.append(field.formField, header);
      }
    });
    tags.forEach(tag => formData.append("tags", tag));
    formData.append("file", buildUploadFile(csv, mapping, cleanup.keptRows));
    return formData;
  };

  const loadPreview = async () => {
    const formData = buildListFormData();
    if (!formData) {
      return false;
    }

    setPreviewing(true);
    try {
      const response = await previewListAPI(formData);
      if (response.status !== "success") {
        message.error(response.detail || response.message || label("errors.previewFailed"));
        return false;
      }
      setPreview(response);
      return true;
    } catch (error) {
      const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      message.error(detail || label("errors.previewFailed"));
      return false;
    } finally {
      setPreviewing(false);
    }
  };

  const createList = async () => {
    const formData = buildListFormData();
    if (!formData) {
      return;
    }

    setCreating(true);
    try {
      getCustomTags(tags, availableTags).forEach(tag => formData.append("custom_tag", tag));

      const response = await uploadListAPI(formData);
      if (response.status !== "success") {
        throw new Error(response.message || response.detail);
      }
      setIsCreated(true);
    } catch (error) {
      message.error((error as Error).message || label("errors.createFailed"));
    } finally {
      setCreating(false);
    }
  };

  const goNext = async () => {
    if (isAdvancingRef.current) {
      return;
    }
    isAdvancingRef.current = true;
    try {
      if (stepKey === "details") {
        const isReady = (await verifyListName()) && (await loadPreview());
        if (!isReady) {
          return;
        }
      }
      if (stepKey === "review") {
        await createList();
        return;
      }
      setShowFieldErrors(false);
      setStepIndex(index => Math.min(index + 1, CREATE_LIST_STEPS.length - 1));
    } finally {
      isAdvancingRef.current = false;
    }
  };

  const handleBlockedNext = () => {
    setShowFieldErrors(true);
    if (stepKey === "upload") {
      message.error(label("errors.uploadRequired"));
    } else if (stepKey === "match") {
      message.error(hasRequiredMapping ? label("errors.noValidEmails") : label("errors.mappingRequired"));
    }
  };

  const goBack = () => {
    if (stepIndex === 0) {
      navigate(AUDIENCE_ROUTE);
      return;
    }
    setShowFieldErrors(false);
    setStepIndex(index => index - 1);
  };

  return {
    stepIndex,
    stepKey,
    csv,
    parsing,
    mapping,
    cleanup,
    peopleCount,
    phoneCount,
    listName,
    description,
    tags,
    availableTags,
    checkingName,
    previewing,
    preview,
    creating,
    isCreated,
    canProceed,
    showFieldErrors,
    selectFile,
    removeFile,
    setFieldMapping,
    setListName,
    setDescription,
    addTag,
    removeTag,
    downloadSkippedRows,
    downloadList,
    goNext,
    goBack,
    handleBlockedNext,
  };
};
